import logger from '@/addons/logger'
import { closeNativeBrowserIfVisible, isNativeMobileApp } from '@/addons/mobile'
import { AuthActions } from '@/store/auth'
import { IAMCallResponse } from '@/store/auth/_state'
import { Browser } from '@capacitor/browser'
import { useStore } from 'vuex'
import { getBaseURL } from '@/addons/functions'

import {
  INavigator,
  IWindow,
  OidcClientSettings,
  RedirectParams,
  User,
  UserManager,
  UserManagerSettings,
} from 'oidc-client-ts'

class InAppBrowserNavigation implements INavigator {
  private readonly _logger = logger

  constructor(protected _settings: UserManagerSettings) {}

  prepare({
    redirectTarget = this._settings.redirectTarget,
  }: RedirectParams): Promise<IWindow> {
    this._logger.info('prepare')

    let targetWindow = window.self as Window
    if (redirectTarget === 'top') {
      targetWindow = window.top ?? window.self
    }

    return {
      navigate: (params): never => {
        return Browser.open({
          url: params.url,
          windowName: '_self',
        }) as unknown as never
      },
      close: () => {
        this._logger.info('close')

        if (isNativeMobileApp()) {
          Browser.close()
        } else {
          targetWindow.close()
        }
      },
    } as unknown as Promise<IWindow>
  }

  callback(): Promise<void> {
    return Promise.resolve()
  }
}

export type OIDCConfig = {
  authorityUrl: string
  clientId: string
  metadataUrl: string
  tokenExchangeUrl: string
  tokenExchange: string
}

export abstract class IAMHelper {
  protected static _userManager: UserManager
  protected static _config: OidcClientSettings
  private static _mobileAppProtocol: string = import.meta.env
    .VITE_OIDC_MOBILE_APP_PROTOCOL
    ? import.meta.env.VITE_OIDC_MOBILE_APP_PROTOCOL
    : 'com.mmfg.pos'

  static getConfigs(config): OIDCConfig {
    return {
      authorityUrl: import.meta.env.VITE_OIDC_AUTHORITY
        ? import.meta.env.VITE_OIDC_AUTHORITY
        : config.OIDC_AUTHORITY,
      clientId: import.meta.env.VITE_OIDC_CLIENT_ID
        ? import.meta.env.VITE_OIDC_CLIENT_ID
        : config.OIDC_CLIENT_ID,
      metadataUrl: import.meta.env.VITE_OIDC_METADATA_PATH
        ? import.meta.env.VITE_OIDC_METADATA_PATH
        : config.OIDC_METADATA_PATH,
      tokenExchangeUrl: import.meta.env.VITE_OIDC_TOKEN_EXCHANGE_URL
        ? import.meta.env.VITE_OIDC_TOKEN_EXCHANGE_URL
        : config.OIDC_TOKEN_EXCHANGE_URL,
      tokenExchange: import.meta.env.VITE_OIDC_TOKEN_EXCHANGE
        ? import.meta.env.VITE_OIDC_TOKEN_EXCHANGE
        : config.OIDC_TOKEN_EXCHANGE,
    }
  }

  static getUserManager(config?): UserManager {
    if (!this._userManager) {
      this.setOpenIDConfig(config)

      const navigation = new InAppBrowserNavigation(this._config)
      this._userManager = new UserManager(this._config, navigation)
    }

    return this._userManager
  }

  static shouldOpenLoginPageInWebView() {
    return isNativeMobileApp()
  }

  static setOpenIDConfig(config) {
    if (!config) {
      return
    }

    const authority = config.authorityUrl
    const clientId = config.clientId

    this._config = {
      authority: authority,
      client_id: clientId,
      response_type: 'code',
      disablePKCE: false,
      metadataUrl: `${authority}/${config.metadataUrl}`,
      response_mode: 'query',
      scope: 'openid email given_name profile family_name internal_id',
      redirect_uri: this.getSigninRedirectUri(),
      metadata: {
        issuer: authority,
        authorization_endpoint: `${authority}/mga/sps/oauth/oauth20/authorize`,
        userinfo_endpoint: `${authority}/mga/sps/oauth/oauth20/userinfo`,
        end_session_endpoint: `${authority}/pkmslogout`,
        token_endpoint: `${authority}/mga/sps/oauth/oauth20/token`,
        introspection_endpoint: `${authority}/mga/sps/oauth/oauth20/introspect`,
        token_endpoint_auth_methods_supported: [
          'private_key_jwt',
          'client_secret_post',
          'client_secret_basic',
          'tls_client_auth',
          'self_signed_tls_client_auth',
        ],
      },
    }
  }

  static getWebAppUrl(): string {
    let url = location.origin
    const baseUrl = getBaseURL()
    if (baseUrl) {
      url += baseUrl
    }
    return url
  }

  static getSigninRedirectUri(): string {
    return isNativeMobileApp()
      ? `${this._mobileAppProtocol}://callback`
      : `${this.getWebAppUrl()}/callback`
  }

  static getSignoutRedirectUri(): string {
    return isNativeMobileApp()
      ? `${this._mobileAppProtocol}://login`
      : `${this.getWebAppUrl()}/login`
  }

  static async closeLoginPageIfVisible() {
    await closeNativeBrowserIfVisible()
  }

  static clearIAMData() {
    localStorage.removeItem('iam_data')
  }

  static storeIAMData(response: User | undefined) {
    if (!response) {
      this.clearIAMData()
      return
    }

    localStorage.setItem('iam_data', JSON.stringify(response))
  }

  static logout() {
    const store = useStore()
    store.dispatch(AuthActions.LOGOUT)
  }

  static getIAMData(): IAMCallResponse | null {
    const data = localStorage.getItem('iam_data')

    if (!data) {
      return null
    }

    const parsedData = JSON.parse(data)

    if (parsedData.expires_at * 1000 < Date.now()) {
      this.clearIAMData()
      this.logout()
      return null
    }

    return parsedData
  }

  static async triggerIAMLogout(config?: OIDCConfig, logoutError?: string) {
    if (!this._userManager && !config) {
      logger.warn('Unable to trigger IAM logout: missing OIDC config')
      return
    }

    const userManager = this.getUserManager(config)
    const postLogoutRedirectUri = logoutError
      ? `${this.getSignoutRedirectUri()}?logoutError=${encodeURIComponent(logoutError)}`
      : this.getSignoutRedirectUri()

    await userManager.signoutRedirect({
      post_logout_redirect_uri: postLogoutRedirectUri,
    })
  }
}
